import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Lock, User, MessageSquare, Brain, Workflow, BarChart3, ExternalLink, AlertTriangle, Shield, KeyRound, Smartphone, Copy, Check, ArrowLeft, ScanLine, ShieldCheck, Info, XCircle, Building2, Eye, EyeOff, CheckCircle, X as XIcon, Timer, ShieldAlert } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { sessionPolicySchema } from '@easyflow/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/lib/utils';
import type { SessionPolicy } from '@easyflow/shared';


interface LoginProps {
  tenantSlug: string;
}

const features = [
  {
    icon: Phone,
    title: 'Sistema PBX VoIP',
    description: 'Centralino cloud completo con IVR, code, gruppi e voicemail',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    icon: MessageSquare,
    title: 'CRM WhatsApp, SMS & Email',
    description: 'Campagne multicanale con WhatsApp Business, SMS e Email integrati',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  {
    icon: Brain,
    title: 'AI Voice Agent',
    description: 'Agenti vocali AI con trascrizione real-time e sentiment analysis',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  {
    icon: Workflow,
    title: 'Workflows Automation',
    description: 'Automazioni visive drag-drop per marketing e CRM',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    icon: BarChart3,
    title: 'Analytics Real Time',
    description: 'Dashboard live con metriche chiamate, performance e KPI',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
];

const PASSWORD_CRITERIA = [
  { key: 'length', label: 'Almeno 12 caratteri', test: (p: string) => p.length >= 12 },
  { key: 'upper', label: 'Almeno una maiuscola (A-Z)', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Almeno una minuscola (a-z)', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number', label: 'Almeno un numero (0-9)', test: (p: string) => /[0-9]/.test(p) },
  { key: 'special', label: 'Almeno un carattere speciale (!@#$%...)', test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(p) },
];

type LoginPhase = 'credentials' | '2fa-verify' | '2fa-setup' | 'force-password-change';

interface SetupData {
  qrCodeDataUri: string;
  manualEntryKey: string;
}


/** Policy di sessione dalla risposta di login: assente/malformata → il motore usa il default condiviso. */
function parseSessionPolicy(candidate: unknown): SessionPolicy | undefined {
  const parsed = sessionPolicySchema.safeParse(candidate);
  return parsed.success ? parsed.data : undefined;
}

export default function Login({ tenantSlug }: LoginProps) {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showActiveSessionDialog, setShowActiveSessionDialog] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [authProvider, setAuthProvider] = useState<'local' | 'ldap'>('local');
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    const fetchAuthProvider = async () => {
      try {
        const res = await fetch(`/api/${tenantSlug}/validate`);
        const data = await res.json();
        if (data.success && data.data?.authProvider) {
          setAuthProvider(data.data.authProvider as 'local' | 'ldap');
        }
        if (data.success && data.data?.ssoEnabled === true) {
          setSsoEnabled(true);
        }
      } catch {}
    };
    fetchAuthProvider();
  }, [tenantSlug]);

  // Task #3236 — consumo del fragment SSO (#sso_token / #sso_error) al ritorno
  // dal callback OIDC. I token viaggiano nel fragment: mai inviati al server.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || (!hash.includes('sso_token=') && !hash.includes('sso_error='))) return;
    const params = new URLSearchParams(hash.slice(1));
    const ssoToken = params.get('sso_token');
    const ssoRefresh = params.get('sso_refresh');
    const ssoErrorCode = params.get('sso_error');
    // Pulizia immediata del fragment (evita token residui in history/URL)
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (ssoErrorCode) {
      const SSO_ERROR_MESSAGES: Record<string, string> = {
        sso_not_enabled: 'SSO non abilitato per questo tenant.',
        sso_denied: 'Accesso negato dal provider di identità.',
        sso_idp_error: 'Errore del provider di identità. Riprova.',
        sso_user_inactive: 'Utente disattivato. Contatta l\'amministratore.',
        sso_identity_conflict: 'Identità non riconciliabile con un account esistente. Contatta l\'amministratore.',
        sso_2fa_required: 'Questo account richiede la verifica 2FA: accedi con email e password.',
        sso_start_failed: 'Impossibile avviare il login SSO. Riprova.',
        sso_callback_failed: 'Errore durante il login SSO. Riprova.',
      };
      toast({
        title: 'Accesso SSO fallito',
        description: SSO_ERROR_MESSAGES[ssoErrorCode] || 'Errore durante il login SSO.',
        variant: 'destructive',
      });
      return;
    }

    if (ssoToken) {
      (async () => {
        try {
          await login(ssoToken, undefined, ssoRefresh ?? undefined);
          toast({ title: 'Login effettuato', description: 'Accesso SSO completato' });
          setPendingRedirect(true);
        } catch {
          toast({
            title: 'Accesso SSO fallito',
            description: 'Token di sessione non valido. Riprova.',
            variant: 'destructive',
          });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- eseguito una volta al mount per consumare il fragment
  }, []);

  const handleSsoLogin = () => {
    window.location.href = `/api/auth/oidc/${encodeURIComponent(tenantSlug)}/start`;
  };

  const isAdMode = authProvider === 'ldap';

  const [phase, setPhase] = useState<LoginPhase>('credentials');
  const [totpCode, setTotpCode] = useState(['', '', '', '', '', '']);
  const [backupMode, setBackupMode] = useState(false);
  const [backupCodeInput, setBackupCodeInput] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [setupStep, setSetupStep] = useState(0);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [forceChangeToken, setForceChangeToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [forceChangeUserName, setForceChangeUserName] = useState('');
  const [passwordChangedSuccess, setPasswordChangedSuccess] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [totpError, setTotpError] = useState('');
  const totpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [lockoutRemainingMs, setLockoutRemainingMs] = useState<number>(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (lockoutUntil === null) return;
    const tick = () => {
      const remaining = lockoutUntil - Date.now();
      if (remaining <= 0) {
        setLockoutRemainingMs(0);
        setLockoutUntil(null);
      } else {
        setLockoutRemainingMs(remaining);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const formatLockoutCountdown = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const isLockedOut = lockoutUntil !== null && lockoutRemainingMs > 0;

  useEffect(() => {
    if (pendingRedirect && isAuthenticated) {
      navigate(`/${tenantSlug}/dashboard`, { replace: true });
    }
  }, [pendingRedirect, isAuthenticated, tenantSlug, navigate]);

  const startTotpSetup = async (token: string) => {
    try {
      const response = await fetch(`/api/totp/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSetupData(data.data);
        setSetupStep(1);
      } else {
        toast({ title: 'Errore', description: data.error || 'Errore nella configurazione 2FA', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Errore di connessione', variant: 'destructive' });
    }
  };

  const performLogin = async (forceLogin: boolean = false, totpCodeStr?: string, backupCode?: string) => {
    setIsLoading(true);
    setTotpError('');

    if (!totpCodeStr && !backupCode) {
      document.cookie = 'session_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      localStorage.removeItem('edgvoip_tenant_token');
      localStorage.removeItem('edgvoip_tenant_user');
      localStorage.removeItem('edgvoip_tenant_refresh_token');
      localStorage.removeItem('edgvoip_tenant_impersonation_token');
      localStorage.removeItem('edgvoip_tenant_impersonation_info');
    }

    const loginPayload: {
      email: string;
      password: string;
      forceLogin?: boolean;
      totpCode?: string;
      backupCode?: string;
    } = {
      email: formData.username,
      password: formData.password,
    };

    if (forceLogin) loginPayload.forceLogin = true;
    if (totpCodeStr) loginPayload.totpCode = totpCodeStr;
    if (backupCode) loginPayload.backupCode = backupCode;

    try {
      const response = await fetch(`/api/${tenantSlug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginPayload),
      });

      const data = await response.json().catch(() => ({ error: 'Errore nella risposta del server' }));

      if (response.status === 409 && data.activeSessions) {
        setShowActiveSessionDialog(true);
        setIsLoading(false);
        return;
      }

      if (data.requires2FA) {
        if (!response.ok) {
          const errMsg = backupCode
            ? 'Il codice di backup inserito non è valido o è già stato usato.'
            : 'Il codice inserito non è corretto. Controlla l\'app di autenticazione e riprova.';
          setTotpError(errMsg);
          toast({
            title: backupCode ? 'Codice di backup non valido' : 'Codice non valido',
            description: errMsg,
            variant: 'destructive',
          });
          setTotpCode(['', '', '', '', '', '']);
          setBackupCodeInput('');
          setTimeout(() => totpInputRefs.current[0]?.focus(), 50);
        } else {
          setTotpError('');
          setPhase('2fa-verify');
          setTimeout(() => totpInputRefs.current[0]?.focus(), 100);
        }
        setIsLoading(false);
        return;
      }

      if (data.requiresTOTPSetup && data.tempToken) {
        setTempToken(data.tempToken);
        setPhase('2fa-setup');
        setSetupStep(0);
        await startTotpSetup(data.tempToken);
        setIsLoading(false);
        return;
      }

      if (data.forcePasswordChange && data.tempToken) {
        setForceChangeToken(data.tempToken);
        setForceChangeUserName(data.user?.firstName || '');
        setPhase('force-password-change');
        setNewPassword('');
        setConfirmPassword('');
        setIsLoading(false);
        return;
      }

      if (response.status === 403) {
        toast({
          title: 'Account disabilitato',
          description: data.error || 'Il tuo account non è attivo. Contatta l\'amministratore.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      if (response.status === 429) {
        // Payload strutturato dal backend (preferito): {locked, remainingSeconds, lockedUntil}
        const lockedUntilIso: string | undefined = data?.lockedUntil;
        const remainingSeconds: number | undefined = typeof data?.remainingSeconds === 'number' ? data.remainingSeconds : undefined;

        let lockedUntilTs: number | null = null;
        if (lockedUntilIso) {
          const parsed = Date.parse(lockedUntilIso);
          if (!Number.isNaN(parsed) && parsed > Date.now()) {
            lockedUntilTs = parsed;
          }
        }
        if (lockedUntilTs === null && typeof remainingSeconds === 'number' && remainingSeconds > 0) {
          lockedUntilTs = Date.now() + remainingSeconds * 1000;
        }

        if (lockedUntilTs !== null) {
          setLockoutUntil(lockedUntilTs);
          setLockoutRemainingMs(lockedUntilTs - Date.now());
          setAttemptsRemaining(null);
        } else {
          // Fallback compatibilità: nessun payload strutturato → solo toast
          toast({
            title: 'Troppi tentativi',
            description: data?.error || 'Hai effettuato troppi tentativi di accesso. Attendi qualche minuto prima di riprovare.',
            variant: 'destructive',
          });
        }
        setIsLoading(false);
        return;
      }

      if (response.status === 401) {
        const remaining = typeof data?.attemptsRemaining === 'number' ? data.attemptsRemaining : null;
        setAttemptsRemaining(remaining);
        toast({
          title: 'Credenziali errate',
          description: 'Email o password non corretti. Verifica e riprova.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const serverMsg = data.error?.message || data.message || data.error;
        toast({
          title: 'Errore del server',
          description: serverMsg || `Si è verificato un errore (${response.status}). Riprova più tardi.`,
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      if (data.success && data.data?.token) {
        await login(
          data.data.token,
          data.data.user,
          data.data.refreshToken,
          parseSessionPolicy(data.data.sessionPolicy),
        );
        toast({
          title: 'Login effettuato',
          description: `Benvenuto in ${data.data.user.tenantSlug}`,
        });
        setPendingRedirect(true);
      } else {
        toast({
          title: 'Errore imprevisto',
          description: 'Risposta del server non valida. Riprova.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      if ((error as { name?: unknown }).name === 'TypeError' && getErrorMessage(error)?.includes('fetch')) {
        toast({
          title: 'Errore di connessione',
          description: 'Impossibile contattare il server. Verifica la tua connessione internet.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Accesso fallito',
          description: getErrorMessage(error) || 'Si è verificato un errore. Riprova.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForcePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: 'Errore', description: 'Le password non coincidono', variant: 'destructive' });
      return;
    }
    const allMet = PASSWORD_CRITERIA.every(c => c.test(newPassword));
    if (!allMet) {
      toast({ title: 'Errore', description: 'La password non rispetta tutti i criteri di sicurezza', variant: 'destructive' });
      return;
    }
    setIsChangingPassword(true);
    try {
      const response = await fetch(`/api/${tenantSlug}/force-change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken: forceChangeToken, newPassword }),
      });
      const data = await response.json();
      if (data.success) {
        toast({
          title: '✅ Password aggiornata con successo',
          description: 'Ora effettua il login inserendo la tua email e la nuova password.',
          duration: 8000,
          variant: 'success',
        });
        setPasswordChangedSuccess(true);
        setPhase('credentials');
        setFormData({ username: formData.username, password: '' });
        setNewPassword('');
        setConfirmPassword('');
        setForceChangeToken('');
      } else {
        toast({
          title: 'Errore',
          description: data.error || (Array.isArray(data.details) ? data.details.join(', ') : 'Errore nel cambio password'),
          variant: 'destructive',
        });
      }
    } catch {
      toast({ title: 'Errore', description: 'Errore di connessione', variant: 'destructive' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const confirmTotpSetup = async () => {
    const code = totpCode.join('');
    if (code.length !== 6) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/totp/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await response.json();
      if (data.success) {
        setBackupCodes(data.data.backupCodes);
        setSetupStep(3);
      } else {
        toast({ title: 'Codice non valido', description: data.error, variant: 'destructive' });
        setTotpCode(['', '', '', '', '', '']);
        totpInputRefs.current[0]?.focus();
      }
    } catch {
      toast({ title: 'Errore', description: 'Errore di connessione', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setTotpError('');
    const newCode = [...totpCode];
    newCode[index] = value.slice(-1);
    setTotpCode(newCode);

    if (value && index < 5) {
      totpInputRefs.current[index + 1]?.focus();
    }

    if (value && newCode.every(d => d !== '')) {
      setTimeout(() => {
        const form = totpInputRefs.current[0]?.closest('form');
        if (form) form.requestSubmit();
      }, 200);
    }
  };

  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
      totpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleTotpPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      e.preventDefault();
      const newCode = [...totpCode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setTotpCode(newCode);
      const nextEmpty = pasted.length < 6 ? pasted.length : 5;
      totpInputRefs.current[nextEmpty]?.focus();

      if (pasted.length === 6) {
        setTimeout(() => {
          const form = totpInputRefs.current[0]?.closest('form');
          if (form) form.requestSubmit();
        }, 200);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await performLogin(false);
  };

  const handleForceLogin = async () => {
    setShowActiveSessionDialog(false);
    // Solleva il flag "login in corso su questa stessa scheda" PRIMA della
    // POST: il backend, su forceLogin, fa broadcast di `session:force-logout`
    // a tutti i socket nella room `user:${user.id}` — incluso un eventuale
    // socket vecchio ancora vivo in QUESTO browser (altra tab già loggata,
    // o stato auth ancora presente). Senza questo flag, il nostro stesso
    // browser riceverebbe il broadcast e mostrerebbe "Sessione terminata"
    // proprio mentre il login sta andando a buon fine.
    // AuthContext.handleForceLogout legge questo flag e ignora l'evento
    // quando è alzato. Cleanup con grazia di 2s per coprire eventuali
    // eventi in volo dal vecchio socket.
    (window as Window & { __edgvoip_login_in_progress?: boolean }).__edgvoip_login_in_progress = true;
    try {
      await performLogin(true);
    } finally {
      setTimeout(() => {
        try { delete (window as Window & { __edgvoip_login_in_progress?: boolean }).__edgvoip_login_in_progress; } catch {}
      }, 2000);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let codeToSend = '';
    let backupToSend = '';

    if (backupMode) {
      backupToSend = backupCodeInput.trim();
      if (!backupToSend) return;
    } else {
      codeToSend = totpCode.join('');
      if (codeToSend.length !== 6) {
        codeToSend = totpInputRefs.current.map(inp => inp?.value || '').join('');
      }
      if (codeToSend.length !== 6) return;
    }

    setIsLoading(true);
    setTotpError('');

    const bodyObj: {
      email: string;
      password: string;
      totpCode?: string;
      backupCode?: string;
    } = {
      email: formData.username,
      password: formData.password,
    };
    if (codeToSend) bodyObj['totpCode'] = codeToSend;
    if (backupToSend) bodyObj['backupCode'] = backupToSend;
    const bodyStr = JSON.stringify(bodyObj);

    try {
      const response = await fetch(`/api/${tenantSlug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyStr,
      });

      const data = await response.json().catch(() => ({ error: 'Errore nella risposta del server' }));

      if (response.status === 409 && data.activeSessions) {
        setShowActiveSessionDialog(true);
        setIsLoading(false);
        return;
      }

      if (data.requires2FA && !response.ok) {
        const errMsg = backupToSend
          ? 'Il codice di backup inserito non è valido o è già stato usato.'
          : 'Il codice inserito non è corretto. Controlla l\'app di autenticazione e riprova.';
        setTotpError(errMsg);
        toast({
          title: backupToSend ? 'Codice di backup non valido' : 'Codice non valido',
          description: errMsg,
          variant: 'destructive',
        });
        setTotpCode(['', '', '', '', '', '']);
        setBackupCodeInput('');
        setTimeout(() => totpInputRefs.current[0]?.focus(), 50);
        setIsLoading(false);
        return;
      }

      if (data.requires2FA && response.ok) {
        setTotpError('Errore: il codice non è stato inviato. Riprova.');
        setTotpCode(['', '', '', '', '', '']);
        setTimeout(() => totpInputRefs.current[0]?.focus(), 50);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        toast({
          title: 'Errore',
          description: data.error || `Errore del server (${response.status})`,
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Branch "cambio password obbligatorio al primo accesso": il backend
      // (`/api/:tenantSlug/login`) esegue il check `force_password_change`
      // DOPO la verifica 2FA. Quando un nuovo utente viene creato con quel
      // flag in un tenant che ha già il 2FA attivo, l'utente fa setup TOTP,
      // poi inserisce il codice 2FA, e a quel punto il backend risponde con
      // `{success: true, forcePasswordChange: true, tempToken}` (200 OK).
      // Senza questa gestione cadeva nell'else generico → toast "Risposta
      // del server non valida" e blocco totale del primo accesso.
      if (data.forcePasswordChange && data.tempToken) {
        setForceChangeToken(data.tempToken);
        setForceChangeUserName(data.user?.firstName || '');
        setPhase('force-password-change');
        setNewPassword('');
        setConfirmPassword('');
        setIsLoading(false);
        return;
      }

      // Edge case difensivo: se per qualunque motivo il backend dovesse
      // ritornare ancora un setup-TOTP a questo punto (es. reset 2FA in
      // corso lato admin), riportiamo l'utente al setup invece di mostrare
      // un errore opaco.
      if (data.requiresTOTPSetup && data.tempToken) {
        setTempToken(data.tempToken);
        setPhase('2fa-setup');
        setSetupStep(0);
        await startTotpSetup(data.tempToken);
        setIsLoading(false);
        return;
      }

      const tokenCandidate = data?.data?.token || data?.token;
      const userCandidate = data?.data?.user || data?.user;
      const refreshCandidate = data?.data?.refreshToken || data?.refreshToken;
      const sessionPolicyCandidate = parseSessionPolicy(data?.data?.sessionPolicy ?? data?.sessionPolicy);

      if (tokenCandidate) {
        await login(tokenCandidate, userCandidate, refreshCandidate, sessionPolicyCandidate);
        toast({
          title: 'Login effettuato',
          description: `Benvenuto in ${userCandidate?.tenantSlug || ''}`,
        });
        setPendingRedirect(true);
      } else {
        try {
          console.error('[Login/2FA] Risposta inattesa dal server:', {
            status: response.status,
            ok: response.ok,
            success: data?.success,
            keys: data && typeof data === 'object' ? Object.keys(data) : [],
            dataKeys: data?.data && typeof data.data === 'object' ? Object.keys(data.data) : [],
          });
        } catch {}
        toast({
          title: 'Errore imprevisto',
          description: 'Risposta del server non valida.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Errore di connessione',
        description: 'Impossibile contattare il server.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'username' && attemptsRemaining !== null) {
      setAttemptsRemaining(null);
    }
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const resetToCredentials = () => {
    setPhase('credentials');
    setTotpCode(['', '', '', '', '', '']);
    setBackupMode(false);
    setBackupCodeInput('');
    setTempToken('');
    setTotpError('');
    setSetupData(null);
    setSetupStep(0);
    setBackupCodes([]);
  };

  const authenticatorApps = [
    {
      name: 'Google Authenticator',
      color: 'bg-blue-50 border-blue-100',
      iconColor: 'text-blue-600',
      svg: (
        <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
          <circle cx="12" cy="12" r="10" fill="#4285F4"/>
          <path d="M12 7v5l3.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="2" fill="white"/>
        </svg>
      ),
      stores: {
        android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
        ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
      },
    },
    {
      name: 'Microsoft Authenticator',
      color: 'bg-sky-50 border-sky-100',
      iconColor: 'text-sky-600',
      svg: (
        <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
          <rect x="3" y="3" width="8" height="8" rx="1" fill="#F25022"/>
          <rect x="13" y="3" width="8" height="8" rx="1" fill="#7FBA00"/>
          <rect x="3" y="13" width="8" height="8" rx="1" fill="#00A4EF"/>
          <rect x="13" y="13" width="8" height="8" rx="1" fill="#FFB900"/>
        </svg>
      ),
      stores: {
        android: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
        ios: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
      },
    },
    {
      name: 'Authy by Twilio',
      color: 'bg-red-50 border-red-100',
      iconColor: 'text-red-500',
      svg: (
        <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
          <circle cx="12" cy="12" r="10" fill="#EC1C24"/>
          <path d="M12 6a6 6 0 100 12 6 6 0 000-12zm0 2a4 4 0 110 8 4 4 0 010-8z" fill="white"/>
          <circle cx="12" cy="12" r="2" fill="white"/>
        </svg>
      ),
      stores: {
        android: 'https://play.google.com/store/apps/details?id=com.authy.authy',
        ios: 'https://apps.apple.com/app/authy/id494168017',
      },
    },
  ];

  const [showManualKey, setShowManualKey] = useState(false);

  const render2FAVerify = () => (
    <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-8 shadow-2xl">
      <div className="text-center mb-6">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
          <Shield className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white">Verifica a due fattori</h2>
        <p className="text-sm text-white/80 mt-1">
          {backupMode
            ? 'Inserisci uno dei codici di backup che hai salvato durante la configurazione'
            : 'Apri la tua app di autenticazione e inserisci il codice a 6 cifre'}
        </p>
      </div>

      {!backupMode && (
        <div className="bg-white/10 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-white/70 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-white/70">
              Il codice si aggiorna ogni 30 secondi. Se il codice non funziona, attendi che ne venga generato uno nuovo.
            </p>
          </div>
        </div>
      )}

      {totpError && (
        <div className="bg-red-500/20 border border-red-400/40 rounded-lg p-3 mb-4 flex items-start gap-2">
          <XCircle className="h-4 w-4 text-red-300 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-200">{totpError}</p>
        </div>
      )}

      <form onSubmit={handle2FASubmit} className="space-y-5">
        {backupMode ? (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-white">Codice di backup (formato XXXX-XXXX)</Label>
            <Input
              value={backupCodeInput}
              onChange={(e) => { setBackupCodeInput(e.target.value); setTotpError(''); }}
              placeholder="XXXX-XXXX"
              className={`bg-white/90 border-white/20 text-gray-900 text-center text-lg font-mono tracking-widest placeholder:text-gray-400 ${totpError ? 'border-red-400 ring-1 ring-red-400' : ''}`}
              disabled={isLoading}
              autoFocus
            />
            <p className="text-xs text-white/60 text-center">
              Ogni codice di backup può essere usato una sola volta
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-white">Codice a 6 cifre</Label>
            <div className="flex gap-2 justify-center" onPaste={handleTotpPaste}>
              {totpCode.map((digit, i) => (
                <Input
                  key={i}
                  ref={(el) => { totpInputRefs.current[i] = el; }}
                  value={digit}
                  onChange={(e) => handleTotpInput(i, e.target.value)}
                  onKeyDown={(e) => handleTotpKeyDown(i, e)}
                  className={`w-11 h-12 text-center text-xl font-bold bg-white/90 border-white/20 text-gray-900 ${totpError ? 'border-red-400 ring-1 ring-red-400' : ''}`}
                  maxLength={1}
                  inputMode="numeric"
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 shadow-lg"
          disabled={isLoading || (!backupMode && totpCode.join('').length !== 6) || (backupMode && !backupCodeInput.trim())}
        >
          {isLoading ? 'Verifica in corso...' : 'Verifica e accedi'}
        </Button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => { setBackupMode(!backupMode); setTotpCode(['', '', '', '', '', '']); setBackupCodeInput(''); setTotpError(''); }}
          className="text-sm text-white/80 hover:text-white transition-colors"
        >
          {backupMode ? 'Usa il codice dall\'app' : 'Non hai accesso all\'app? Usa un codice di backup'}
        </button>
        <button
          type="button"
          onClick={resetToCredentials}
          className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Torna al login
        </button>
      </div>
    </div>
  );

  const [showAppHelp, setShowAppHelp] = useState(false);

  const setupStepLabels = [
    { icon: ScanLine, label: 'Scansiona QR' },
    { icon: Shield, label: 'Verifica' },
    { icon: KeyRound, label: 'Backup' },
  ];

  const render2FASetup = () => (
    <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-6 shadow-2xl">
      <div className="text-center mb-4">
        <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-3">
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-xl font-bold text-white">Configura autenticazione 2FA</h2>
        <p className="text-xs text-white/80 mt-1">
          Il tuo amministratore richiede la verifica in due passaggi per proteggere il tuo account
        </p>
      </div>

      <div className="flex items-center justify-center gap-1 mb-5">
        {setupStepLabels.map((step, i) => {
          const StepIcon = step.icon;
          const isActive = i + 1 <= setupStep;
          const isCurrent = i + 1 === setupStep;
          return (
            <div key={i} className="flex items-center gap-1">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                isCurrent ? 'bg-white text-blue-600' :
                isActive ? 'bg-white/30 text-white' : 'bg-white/10 text-white/40'
              }`}>
                <StepIcon className="h-3 w-3" />
                <span className="hidden sm:inline">{step.label}</span>
              </div>
              {i < 2 && <div className={`w-3 h-0.5 rounded ${isActive ? 'bg-white/50' : 'bg-white/15'}`} />}
            </div>
          );
        })}
      </div>

      {setupStep === 0 && (
        <div className="text-center space-y-4">
          <p className="text-white/90 text-sm">Preparazione in corso...</p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        </div>
      )}

      {setupStep === 1 && setupData && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">
              <ScanLine className="h-4 w-4 text-blue-600" />
              Passo 1: Scansiona il QR code
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Apri la tua app di autenticazione, tocca il pulsante <strong>"+"</strong> e inquadra questo QR code con la fotocamera.
            </p>

            <div className="flex justify-center bg-gray-50 rounded-lg p-4">
              <img src={setupData.qrCodeDataUri} alt="QR Code per autenticazione 2FA" className="w-48 h-48" />
            </div>

            <button
              type="button"
              onClick={() => setShowManualKey(!showManualKey)}
              className="w-full mt-3 text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1"
            >
              {showManualKey ? 'Nascondi codice manuale' : 'Non riesci a scansionare? Inserisci il codice manualmente'}
            </button>

            {showManualKey && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-xs text-amber-800 mb-1.5 font-medium">Codice manuale:</p>
                <p className="text-xs text-gray-500 mb-2">
                  Nell'app, scegli "Inserisci codice manualmente" e copia questo codice:
                </p>
                <div className="bg-white rounded p-2 border border-amber-200">
                  <p className="text-sm font-mono text-center text-gray-900 break-all select-all tracking-wider">
                    {setupData.manualEntryKey}
                  </p>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowAppHelp(!showAppHelp)}
              className="w-full mt-2 text-xs text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1"
            >
              <Info className="h-3 w-3" />
              {showAppHelp ? 'Nascondi suggerimenti app' : 'Non hai un\'app di autenticazione?'}
            </button>

            {showAppHelp && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2">
                <p className="text-xs text-blue-800 font-medium">App consigliate (gratuite):</p>
                <div className="space-y-1.5">
                  {authenticatorApps.map((app) => (
                    <div key={app.name} className="flex items-center gap-2 text-xs text-gray-700">
                      <div className="flex-shrink-0 w-5 h-5">{app.svg}</div>
                      <span className="font-medium">{app.name}</span>
                      <span className="text-gray-400">—</span>
                      <a href={app.stores.android} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                        Android <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                      <a href={app.stores.ios} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                        iOS <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-blue-600 mt-1">
                  Funziona con qualsiasi app compatibile TOTP.
                </p>
              </div>
            )}
          </div>

          <Button
            onClick={() => { setSetupStep(2); setTotpCode(['', '', '', '', '', '']); setTimeout(() => totpInputRefs.current[0]?.focus(), 100); }}
            className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 shadow-lg"
          >
            Ho aggiunto l'account, continua
          </Button>
        </div>
      )}

      {setupStep === 2 && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-blue-600" />
              Passo 2: Verifica la configurazione
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Inserisci il codice a 6 cifre che vedi nella tua app di autenticazione per confermare che tutto funzioni correttamente.
            </p>

            <form onSubmit={(e) => { e.preventDefault(); confirmTotpSetup(); }}>
              <div className="flex gap-2 justify-center mb-4" onPaste={handleTotpPaste}>
                {totpCode.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => { totpInputRefs.current[i] = el; }}
                    value={digit}
                    onChange={(e) => handleTotpInput(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    className="w-11 h-13 text-center text-xl font-bold bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    maxLength={1}
                    inputMode="numeric"
                    disabled={isLoading}
                  />
                ))}
              </div>

              <p className="text-[10px] text-gray-400 text-center mb-3">
                Il codice si rinnova ogni 30 secondi
              </p>

              <Button
                type="submit"
                className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold py-2.5"
                disabled={isLoading || totpCode.join('').length !== 6}
              >
                {isLoading ? 'Verifica in corso...' : 'Verifica e attiva il 2FA'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {setupStep === 3 && backupCodes.length > 0 && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="h-5 w-5 text-amber-600" />
              <h3 className="text-sm font-semibold text-gray-800">Passo 3: Salva i codici di backup</h3>
            </div>

            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-3">
              <p className="text-xs text-amber-800 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span><strong>Importante:</strong> Questi codici ti permettono di accedere se perdi il telefono o non hai accesso all'app. Salvali in un luogo sicuro (es. gestore password, stampa cartacea). Ogni codice funziona una sola volta.</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {backupCodes.map((code, i) => (
                <div key={i} className="text-sm font-mono text-center py-2 px-2 bg-gray-50 rounded border border-gray-100 text-gray-800 select-all">
                  {code}
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={copyBackupCodes}
            >
              {copiedCodes ? <><Check className="h-4 w-4 mr-1.5" /> Codici copiati negli appunti!</> : <><Copy className="h-4 w-4 mr-1.5" /> Copia tutti i codici</>}
            </Button>
          </div>

          <div className="bg-white/10 rounded-lg p-3">
            <p className="text-xs text-white/80 flex items-start gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              Il 2FA è stato attivato con successo! Da ora, ogni accesso richiederà il codice dalla tua app oltre alla password.
            </p>
          </div>

          <Button
            onClick={() => {
              setPhase('2fa-verify');
              setTotpCode(['', '', '', '', '', '']);
              setBackupMode(false);
              setBackupCodeInput('');
              setTimeout(() => totpInputRefs.current[0]?.focus(), 100);
            }}
            className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 shadow-lg"
          >
            Ho salvato i codici, accedi ora
          </Button>
        </div>
      )}

      {setupStep > 0 && setupStep < 3 && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={resetToCredentials}
            className="text-sm text-white/50 hover:text-white transition-colors flex items-center gap-1 mx-auto"
          >
            <ArrowLeft className="h-3 w-3" /> Torna al login
          </button>
        </div>
      )}
    </div>
  );

  const renderForcePasswordChange = () => {
    const allMet = PASSWORD_CRITERIA.every(c => c.test(newPassword));
    const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

    return (
      <div className="rounded-2xl p-8 shadow-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-red-500">
        <div className="text-center mb-6">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
            <ShieldCheck className="h-9 w-9 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Cambio Password Obbligatorio</h2>
          <p className="text-white/80 text-sm mt-2">
            {forceChangeUserName ? `Ciao ${forceChangeUserName}, ` : ''}per motivi di sicurezza devi impostare una nuova password.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-white/90 text-sm font-medium">Nuova Password</Label>
            <div className="relative mt-1">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Inserisci la nuova password"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 pr-10 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-white/90 text-sm font-medium">Conferma Password</Label>
            <div className="mt-1">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la nuova password"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 font-mono"
              />
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-200 text-xs mt-1 flex items-center gap-1">
                <XIcon className="h-3 w-3" /> Le password non coincidono
              </p>
            )}
            {passwordsMatch && (
              <p className="text-green-200 text-xs mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Le password coincidono
              </p>
            )}
          </div>

          {newPassword && (
            <div className="bg-white/10 rounded-lg p-3 space-y-1.5">
              <p className="text-white/90 text-xs font-semibold mb-2">Criteri di sicurezza:</p>
              {PASSWORD_CRITERIA.map(criterion => {
                const passed = criterion.test(newPassword);
                return (
                  <div key={criterion.key} className="flex items-center gap-1.5 text-xs">
                    {passed ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-300 shrink-0" />
                    ) : (
                      <XIcon className="h-3.5 w-3.5 text-red-300 shrink-0" />
                    )}
                    <span className={passed ? 'text-green-200' : 'text-white/60'}>
                      {criterion.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <Button
            onClick={handleForcePasswordChange}
            disabled={!allMet || !passwordsMatch || isChangingPassword}
            className="w-full bg-white text-orange-600 hover:bg-white/90 font-semibold h-12 text-base"
          >
            {isChangingPassword ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Cambio in corso...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Imposta Nuova Password
              </span>
            )}
          </Button>

          <button
            type="button"
            onClick={() => {
              setPhase('credentials');
              setForceChangeToken('');
              setNewPassword('');
              setConfirmPassword('');
            }}
            className="w-full text-center text-white/70 hover:text-white text-sm underline mt-2"
          >
            Torna al login
          </button>
        </div>
      </div>
    );
  };

  const renderCredentials = () => (
    <div className={`rounded-2xl p-8 shadow-2xl ${isAdMode ? 'bg-gradient-to-br from-sky-700 via-sky-600 to-blue-700' : 'bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600'}`}>
      <div className="text-center mb-8">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
          {isAdMode ? (
            <svg viewBox="0 0 24 24" className="h-9 w-9" fill="none">
              <rect x="1" y="1" width="10" height="10" rx="1" fill="#F25022"/>
              <rect x="13" y="1" width="10" height="10" rx="1" fill="#7FBA00"/>
              <rect x="1" y="13" width="10" height="10" rx="1" fill="#00A4EF"/>
              <rect x="13" y="13" width="10" height="10" rx="1" fill="#FFB900"/>
            </svg>
          ) : (
            <img src="/logos/easyflow-icon-v5.svg" alt="EasyFlow" className="h-10 w-10" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-white">
          {isAdMode ? 'Accesso Active Directory' : 'Accedi al tuo account'}
        </h2>
        <p className="text-sm text-white/80 mt-1">
          {isAdMode ? 'Usa le credenziali del tuo dominio aziendale' : 'Inserisci le tue credenziali'}
        </p>
        {isAdMode && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-xs text-white/90">
            <Shield className="h-3 w-3" />
            Single Sign-On
          </div>
        )}
      </div>

      {!isLockedOut && attemptsRemaining !== null && attemptsRemaining > 0 && attemptsRemaining <= 2 && (
        <div
          role="alert"
          className="mb-5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 border border-amber-300 shadow-lg shadow-amber-900/30 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300"
          data-testid="login-attempts-warning-banner"
        >
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Attenzione: tentativi quasi esauriti</p>
            <p className="text-xs text-white/95 mt-1 leading-relaxed">
              {attemptsRemaining === 1 ? (
                <>Hai <strong className="font-semibold">1 ulteriore tentativo</strong> prima che l'account venga bloccato per 5 minuti per motivi di sicurezza.</>
              ) : (
                <>Hai ancora <strong className="font-semibold">{attemptsRemaining} tentativi</strong> prima che l'account venga bloccato per 5 minuti per motivi di sicurezza.</>
              )}
            </p>
          </div>
        </div>
      )}

      {isLockedOut && (
        <div
          role="alert"
          className="mb-5 rounded-lg bg-gradient-to-r from-red-700 to-rose-800 border border-red-400 shadow-lg shadow-red-900/40 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300"
          data-testid="login-lockout-banner"
        >
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Account temporaneamente bloccato</p>
            <p className="text-xs text-white/90 mt-1 leading-relaxed">
              Troppi tentativi di accesso falliti. Per motivi di sicurezza il login è disabilitato.
              Potrai riprovare automaticamente allo scadere del timer.
            </p>
            <div className="mt-2 inline-flex items-center gap-1.5 bg-white/15 px-2.5 py-1 rounded-md text-white">
              <Timer className="h-3.5 w-3.5" />
              <span
                className="text-sm font-mono font-semibold tracking-wider"
                data-testid="login-lockout-countdown"
              >
                {formatLockoutCountdown(lockoutRemainingMs)}
              </span>
            </div>
          </div>
        </div>
      )}

      {passwordChangedSuccess && (
        <div className="mb-5 rounded-lg bg-gradient-to-r from-emerald-700 to-green-800 border border-emerald-400 shadow-lg shadow-emerald-900/40 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="shrink-0 mt-0.5 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow-sm">
            <svg className="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white drop-shadow-sm">Password aggiornata con successo</p>
            <p className="text-xs text-white/95 mt-1 leading-relaxed">
              Inserisci la tua email e la <strong className="font-semibold underline decoration-white/60 underline-offset-2">nuova password</strong> per completare l'accesso.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium text-white">
            {isAdMode ? 'Credenziali aziendali' : 'Email'}
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="username"
              name="username"
              type="text"
              required
              className="bg-white/90 border-white/20 pl-10 focus-visible:ring-white/50 text-gray-900 placeholder:text-gray-500 transition-all duration-200"
              placeholder={isAdMode ? 'utente@dominio.com o username' : 'Inserisci la tua email'}
              value={formData.username}
              onChange={handleInputChange}
              disabled={isLoading || isLockedOut}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-white">
            {isAdMode ? 'Password di dominio' : 'Password'}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              className="bg-white/90 border-white/20 pl-10 pr-10 focus-visible:ring-white/50 text-gray-900 placeholder:text-gray-500 transition-all duration-200"
              placeholder={isAdMode ? 'Password di dominio' : 'Inserisci la tua password'}
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading || isLockedOut}
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              disabled={isLoading || isLockedOut}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              aria-controls="password"
              aria-pressed={showPassword}
              data-testid="button-toggle-password"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className={`w-full font-semibold py-3 hover:scale-[1.02] transition-all duration-200 shadow-lg ${isAdMode ? 'bg-white text-sky-700 hover:bg-gray-100' : 'bg-white text-blue-600 hover:bg-gray-100'} disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100`}
          disabled={isLoading || isLockedOut}
        >
          {isLockedOut
            ? `Riprova tra ${formatLockoutCountdown(lockoutRemainingMs)}`
            : isLoading
              ? 'Accesso in corso...'
              : isAdMode
                ? 'Accedi con Active Directory'
                : 'Accedi'}
        </Button>
      </form>

      {ssoEnabled && (
        <div className="mt-4">
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-white/20" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-transparent px-2 text-white/60">oppure</span>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleSsoLogin}
            disabled={isLoading}
            className="w-full font-semibold py-3 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white transition-all duration-200"
          >
            <KeyRound className="h-4 w-4 mr-2" />
            Accedi con SSO
          </Button>
        </div>
      )}

      {!isAdMode && (
        <div className="mt-4 text-center">
          <Link
            to={`/${tenantSlug}/forgot-password`}
            className="text-sm text-white/90 hover:text-white transition-colors font-medium hover:underline"
          >
            Password dimenticata?
          </Link>
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-white/20 text-center">
        {isAdMode ? (
          <div className="flex items-center justify-center gap-2 text-xs text-white/50">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 opacity-60" fill="none">
              <rect x="1" y="1" width="10" height="10" rx="1" fill="currentColor"/>
              <rect x="13" y="1" width="10" height="10" rx="1" fill="currentColor"/>
              <rect x="1" y="13" width="10" height="10" rx="1" fill="currentColor"/>
              <rect x="13" y="13" width="10" height="10" rx="1" fill="currentColor"/>
            </svg>
            <span>Microsoft Active Directory</span>
          </div>
        ) : (
          <span className="text-xs text-white/60">
            crafted by{' '}
            <a
              href="https://www.easydigitalgroup.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline"
            >
              easydigitalgroup
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col lg:flex-row min-h-screen">
        <div className="lg:w-1/2 flex flex-col justify-center p-8 lg:p-12 xl:p-16">
          <div className="mb-8 lg:mb-12">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logos/easyflow-full-v3.svg" alt="EasyFlow" className="h-[67px]" />
            </div>
            <p className="text-lg text-muted-foreground">
              La piattaforma AI-powered per la gestione completa delle comunicazioni aziendali
            </p>
          </div>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${feature.iconBg} flex items-center justify-center`}>
                  <feature.icon className={`h-6 w-6 ${feature.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            {phase === 'credentials' && renderCredentials()}
            {phase === '2fa-verify' && render2FAVerify()}
            {phase === '2fa-setup' && render2FASetup()}
            {phase === 'force-password-change' && renderForcePasswordChange()}
          </div>
        </div>
      </div>

      <AlertDialog open={showActiveSessionDialog} onOpenChange={setShowActiveSessionDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Sessione gi&agrave; attiva</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-muted-foreground">
              Il tuo account risulta gi&agrave; connesso da un altro dispositivo o browser.
              Continuando, la sessione precedente verr&agrave; chiusa automaticamente e
              l'altro dispositivo verr&agrave; disconnesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowActiveSessionDialog(false)}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceLogin}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Continua e disconnetti l'altra sessione
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
